/**
 * Servicios para la gestión de categorías.
 *
 * Este módulo contiene todos los servicios necesarios para interactuar con la API
 * del backend en relación a las categorías, incluyendo operaciones de creación,
 * actualización, eliminación y consulta de información.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-06]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { fetchWrapper } from "../../../fetchWrapper";

export const CategoriesListPageService = async (pageIndex) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/categories/page`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      const result = await response.json();
      return result;
    } else {
      throw new Error(`Error del servidor al realizar la solicitud`);
    }
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};

export const CategoryCreateService = async (payload) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/categories/create`;

    // Llamar a fetchWrapper para hacer la solicitud POST
    const result = await fetchWrapper(url, {
      method: "POST",
      body: JSON.stringify(payload), // Pasar el payload en el body
    });

    return result;
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};

/**
 * Servicio para obtener información de una categoría específica
 * @param {number} categoryId - ID de la categoría
 * @returns {Object} Respuesta del servidor con los datos de la categoría
 */
export const CategoryGetByIdService = async (categoryId) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/categories/${categoryId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      const result = await response.json();
      return result;
    } else {
      throw new Error(`Error del servidor al realizar la solicitud`);
    }
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};

/**
 * Servicio para actualizar una categoría existente
 * @param {number} categoryId - ID de la categoría a actualizar
 * @param {Object} payload - Datos actualizados de la categoría
 * @returns {Object} Respuesta del servidor
 */
export const CategoryUpdateService = async (categoryId, payload) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/categories/update/${categoryId}`;

    // Llamar a fetchWrapper para hacer la solicitud PUT
    const result = await fetchWrapper(url, {
      method: "PUT",
      body: JSON.stringify(payload),
    });

    return result;
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};

/**
 * Servicio para eliminar una categoría
 * @param {number} categoryId - ID de la categoría a eliminar
 * @returns {Object} Respuesta del servidor
 */
export const CategoryDeleteService = async (categoryId) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/categories/delete/${categoryId}`;

    // Llamar a fetchWrapper para hacer la solicitud DELETE
    const result = await fetchWrapper(url, {
      method: "DELETE",
    });

    return result;
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};
